import React, { useCallback, type FC } from "react";

import { useIntl } from "react-intl";

import { useRecoilCallback, useRecoilValue } from "recoil";

import {
	BUTTON_VARIANT_PRIMARY,
	Button,
	ContextMenu,
	Flex,
	IconArrow1Down,
	makeButtonWithMenu,
} from "mailup-web-components";

import {
	STORE_KEY_LOGIN_TYPE,
	evalCondition,
	storeGet,
} from "mailup-react-helpers";

import { LOGIN_TYPE_ADMLIMITED } from "Constants";

import { MAX_RECIPIENTS_EXPORT_XLSX } from "Pages/RecipientsList/model";
import { bulkExportCSVCallback } from "Pages/RecipientsList/recoil/callbacks/bulk";
import { modalBulkAddToGroupOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalBulkAddToGroup";
import { modalBulkFindReplaceOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalBulkFindReplace";
import { modalBulkRemoveFromGroupsOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalBulkRemoveFromGroups";
import { modalBulkUnsubscribeOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalBulkUnsubscribe";
import {
	exportSelectionCountState,
	selectionCountState,
} from "Pages/RecipientsList/recoil/state/results";
import { gainsightTrack } from "mailup-react-helpers";

const MenuComponent: FC = () => {
	const intl = useIntl();

	const count = useRecoilValue(selectionCountState);
	const exportCount = useRecoilValue(exportSelectionCountState);

	const modalBulkUnsubscribe = useRecoilCallback(
		modalBulkUnsubscribeOpenCallback,
		[],
	);

	const modalBulkFindReplace = useRecoilCallback(
		modalBulkFindReplaceOpenCallback,
		[],
	);

	const modalBulkAddToGroup = useRecoilCallback(
		modalBulkAddToGroupOpenCallback,
		[],
	);

	const modalBulkRemoveFromGroups = useRecoilCallback(
		modalBulkRemoveFromGroupsOpenCallback,
		[],
	);

	const bulkExport = useRecoilCallback(bulkExportCSVCallback, []);

	const bulkExportCSV = useCallback(() => {
		gainsightTrack(
			"mup_recipients_click_exportCSVSelectedRecipientsActionMenu",
			{
				
			},
		);

		void bulkExport("csv");
	}, [bulkExport]);

	const bulkExportXLSX = useCallback(() => {
		void bulkExport("xlsx");
	}, [bulkExport]);

	const loginType = storeGet(STORE_KEY_LOGIN_TYPE);
	const exportPermission = evalCondition("NOT uo:NOEXPORT") as boolean;
	const canExport = exportPermission && loginType !== LOGIN_TYPE_ADMLIMITED;

	const canEdit = evalCondition("uo:EDIT") as boolean;

	const handleBulkUnsubscribe = (): void => {
		gainsightTrack("mup_recipients_click_unsubscribeActionBulk", {});
		void modalBulkUnsubscribe();
	};

	const bulkDisabled = count <= 0;

	const menuItems = [
		{
			"data-mu-test": "bulk-unsubscribe",
			label: intl.formatMessage({
				id: "Load.RecipientsList.BulkActionUnsubscribe",
			}),
			disabled: bulkDisabled,
			onClick: handleBulkUnsubscribe,
		},
		...(canEdit
			? [
					{
						"data-mu-test": "bulk-find-replace",
						label: intl.formatMessage({
							id: "Load.RecipientsList.BulkActionFindReplaceEmpty",
						}),
						disabled: bulkDisabled,
						onClick: modalBulkFindReplace,
					},
				]
			: []),
		{
			"data-mu-test": "bulk-add-to-group",
			label: intl.formatMessage({
				id: "Load.RecipientsList.BulkActionAddToGroup",
			}),
			disabled: bulkDisabled,
			separator: true,
			onClick: modalBulkAddToGroup,
		},
		{
			"data-mu-test": "bulk-remove-from-groups",
			label: intl.formatMessage({
				id: "Load.RecipientsList.BulkActionRemoveFromGroup",
			}),
			disabled: bulkDisabled,
			onClick: modalBulkRemoveFromGroups,
		},
		...(canExport
			? [
					{
						"data-mu-test": "bulk-export-csv",
						label: intl.formatMessage({
							id: "Load.RecipientsList.BulkActionExportCSV",
						}),
						separator: true,
						onClick: bulkExportCSV,
					},
					{
						"data-mu-test": "bulk-export-xlsx",
						label: intl.formatMessage({
							id: "Load.RecipientsList.BulkActionExportXLSX",
						}),
						disabled: exportCount > MAX_RECIPIENTS_EXPORT_XLSX,
						onClick: bulkExportXLSX,
					},
				]
			: []),
	];

	return <ContextMenu size="md" items={menuItems} />;
};

const ButtonComponent: FC = () => {
	const intl = useIntl();

	return (
		<Button kind={BUTTON_VARIANT_PRIMARY} disabled={false}>
			<Flex alignItems="center" gap="8px">
				{intl.formatMessage({ id: "Load.RecipientsList.BatchActions" })}
				<IconArrow1Down />
			</Flex>
		</Button>
	);
};

const BatchActions = makeButtonWithMenu(ButtonComponent, MenuComponent);

export default BatchActions;
